import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Input, message, Modal, Popconfirm } from 'antd';
import { AssetSuggestion } from '../../types/InvoiceDecisionTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ASSET_FIELD_LABELS, getSuggestedAssetFieldUpdates } from '../../utils/InvoiceUtils';

interface EnrichAssetModalProps {
	visible: boolean;
	assetSuggestion: AssetSuggestion;
	asset: any;
	applying?: boolean;
	onEnrich: (updates: any) => Promise<any> | void;
	onCancel: () => void;
}

/**
 * Review and apply what the invoice printed about the work order's asset.
 *
 * Every value is editable and every row is opt-in: the backend only ever suggests, and a value it
 * read off a PDF is a starting point for a person, not an answer. Rows whose value the asset
 * record already carries are dropped — once applied, there is nothing left to review, which is
 * also what stops the banner reappearing after a successful enrich.
 */
export const EnrichAssetModal: React.FC<EnrichAssetModalProps> = ({
	visible,
	assetSuggestion,
	asset,
	applying,
	onEnrich,
	onCancel,
}) => {
	const fields = useMemo(
		() => getSuggestedAssetFieldUpdates(assetSuggestion, asset),
		[assetSuggestion, asset]
	);

	// Keyed by field so a re-render cannot reorder them. Every row starts UNSELECTED: since the
	// backend stopped gating suggestions on confidence, a field arrives with its probability
	// attached and no floor under it — a blank field used to need >0.9 and now comes through at any
	// value. Pre-checking would turn a low-probability guess into the default, so each row is an
	// explicit choice and ENRICH stays disabled until one is made.
	const [selectedByField, setSelected] = useState<any>({});
	const [edited, setEdited] = useState<any>({});

	const valueFor = useCallback(
		(field) => (edited[field.field] !== undefined ? edited[field.field] : field.invoiceValue),
		[edited]
	);

	const checkedFields = useMemo(
		() => fields.filter((field) => selectedByField[field.field]),
		[fields, selectedByField]
	);

	const overwritesExisting = useMemo(
		() => checkedFields.some((field) => !!field.currentValue),
		[checkedFields]
	);

	const handleToggle = useCallback(
		(field) => (event) => {
			setSelected({ ...selectedByField, [field.field]: event.target.checked });
		},
		[selectedByField]
	);

	const handleChange = useCallback(
		(field) => (event) => {
			setEdited((previous) => ({ ...previous, [field.field]: event.target.value }));
		},
		[]
	);

	const handleEnrich = useCallback(() => {
		const updates = {};
		checkedFields.forEach((field) => {
			const value = `${valueFor(field)}`.trim();
			if (value === '') {
				return;
			}
			updates[field.assetField] = value;
		});
		if (Object.keys(updates).length === 0) {
			message.error('Nothing to enrich — every selected field is empty.');
			return Promise.resolve();
		}
		// Handed back to the Popconfirm: a returned promise makes its confirm button show a spinner
		// and hold the popover open until the write settles.
		return onEnrich(updates);
	}, [checkedFields, valueFor, onEnrich]);

	const assetTypeName =
		nullSafeGet('assetType.name', asset) || nullSafeGetOrElse('assetTypeName', asset, '');
	const assetId = nullSafeGet('assetId', assetSuggestion);

	return (
		<Modal
			visible={visible}
			width={572}
			onCancel={onCancel}
			bodyStyle={{ padding: 0 }}
			title={
				<div className="flex flex-col gap-y-2xs">
					<div className="h5medium text-character-primary">Enrich Data?</div>
					<div className="bodyregular text-character-secondary">
						We found invoice details that can help update the fields below. Please review.
					</div>
				</div>
			}
			footer={
				<div className="flex flex-row items-center justify-end gap-x-s">
					<Button onClick={onCancel}>SKIP</Button>
					<Popconfirm
						// Always confirm: this writes to a record the invoice is only evidence about.
						title={
							<div className="flex max-w-[320px] flex-col gap-y-2">
								<div className="h5medium text-character-primary">
									{overwritesExisting ? 'Overwrite existing data?' : 'Enrich asset record?'}
								</div>
								<div className="bodyregular text-character-primary">
									{overwritesExisting
										? 'Enriching these fields will overwrite existing data.'
										: 'These values will be written to the asset record.'}
								</div>
							</div>
						}
						okText="Confirm"
						cancelText="Cancel"
						onConfirm={handleEnrich}
						disabled={checkedFields.length === 0}
					>
						<Button type="primary" loading={applying} disabled={checkedFields.length === 0}>
							ENRICH ({checkedFields.length})
						</Button>
					</Popconfirm>
				</div>
			}
		>
			<div className="border-0 border-b border-solid border-grey-4 bg-grey-3 px-l py-xs">
				<div className="bodyregular text-character-primary">
					{nullSafeGetOrElse('name', asset, `Asset ${assetId}`)}
				</div>
				<div className="footnoteregular flex flex-row gap-x-2xs text-character-secondary">
					{assetTypeName ? <span>{assetTypeName}</span> : null}
					{assetTypeName ? <span>•</span> : null}
					<span>Asset ID {assetId}</span>
				</div>
			</div>
			<div className="flex flex-col p-l">
				{fields.map((field) => {
					const isChecked = !!selectedByField[field.field];
					return (
						<div className="flex flex-row items-start gap-x-s pb-l" key={field.field}>
							<div className="flex h-6 items-center">
								<Checkbox checked={isChecked} onChange={handleToggle(field)} />
							</div>
							<div className="flex flex-1 flex-col">
								<div className="bodyregular pb-2 text-character-primary">
									{ASSET_FIELD_LABELS[field.field] || field.field}:
								</div>
								<Input
									value={valueFor(field)}
									disabled={!isChecked}
									onChange={handleChange(field)}
								/>
								{/* Only meaningful when the record already holds something this would replace. */}
								{field.currentValue ? (
									<div className="footnoteregular pt-1 text-character-secondary">
										{field.currentValue} → {valueFor(field)}
									</div>
								) : null}
							</div>
						</div>
					);
				})}
			</div>
		</Modal>
	);
};

export default EnrichAssetModal;
